import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FileSaver from "file-saver";
import { useEffect, useState } from "react";

import { GlobalState } from "../../../context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import ResultTable from "../ResultTable";

const uploadButtonSx = {
	textTransform: "none",
	fontWeight: 600,
	borderRadius: 1.5,
	bgcolor: "#1f2937",
	boxShadow: "none",
	"&:hover": { bgcolor: "#111827", boxShadow: "none" },
};

const outlineButtonSx = {
	textTransform: "none",
	fontWeight: 600,
	borderRadius: 1.5,
	borderColor: "#d1d5db",
	color: "#374151",
	boxShadow: "none",
	"&:hover": { borderColor: "#9ca3af", bgcolor: "#f9fafb", boxShadow: "none" },
};

const Mapping = ({ active = true }) => {
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const [rows, setRows] = useState([]);
	const [file, setFile] = useState(null);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);

	const notify = (message, severity) => {
		setSnackBarOpen(true);
		setSnackBar({
			...snackBar,
			message,
			severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		});
	};

	useEffect(() => {
		if (!active) return undefined;
		let cancelled = false;
		setLoading(true);
		pythonVmInstance
			.get("/uc_vs_ginesys/mapping")
			.then((response) => {
				if (cancelled) return;
				const data = response?.data?.result;
				setRows(Array.isArray(data) ? data : []);
			})
			.catch((error) => {
				if (cancelled) return;
				setRows([]);
				setSnackBarOpen(true);
				setSnackBar((current) => ({
					...current,
					message: error?.response?.data?.message || "Failed to load mapping rows",
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				}));
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [active, setSnackBar, setSnackBarOpen]);

	const handleFileSelect = (event) => {
		const next = event.target.files?.[0] || null;
		event.target.value = "";
		if (!next) return;
		const name = next.name.toLowerCase();
		if (!name.endsWith(".csv") && !name.endsWith(".xlsx") && !name.endsWith(".xls")) {
			setFile(null);
			notify("Upload a CSV or Excel file", "error");
			return;
		}
		setFile(next);
	};

	const downloadTemplate = () => {
		const blob = new Blob(["uc_invoice_no,uc_mapping_invoice_no,gn_invoice_no,gn_mapping_invoice_no\n"], { type: "text/csv;charset=utf-8" });
		FileSaver.saveAs(blob, "uc_gn_mapping_template.csv");
	};

	const handleUpload = async () => {
		if (!file) {
			notify("Choose a file to upload", "error");
			return;
		}
		setUploading(true);
		try {
			const form = new FormData();
			form.append("file", file);
			const response = await pythonVmInstance.post("/uc_vs_ginesys/mapping", form, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			const data = response?.data?.result;
			setRows(Array.isArray(data) ? data : []);
			setFile(null);
			notify(response?.data?.message || "Mapping file uploaded", "success");
		} catch (error) {
			notify(error?.response?.data?.message || "Failed to upload the mapping file", "error");
		} finally {
			setUploading(false);
		}
	};

	return (
		<Stack spacing={2.5}>
			<Box
				sx={{
					border: "1px solid #e6e8ec",
					borderRadius: 2,
					bgcolor: "#fafbfc",
					p: { xs: 1.5, sm: 2 },
				}}>
				<Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "1.05rem" }}>Mapping</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
					Upload a CSV or Excel file with uc_invoice_no, uc_mapping_invoice_no, gn_invoice_no, and gn_mapping_invoice_no. Uploaded rows are added below with who uploaded them and when.
				</Typography>
				<Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ sm: "center" }} flexWrap="wrap">
					<Button component="label" variant="outlined" startIcon={<UploadFileRoundedIcon />} sx={outlineButtonSx}>
						Choose file
						<input hidden type="file" accept=".csv,.xlsx,.xls,text/csv" onChange={handleFileSelect} />
					</Button>
					<Button variant="contained" onClick={handleUpload} disabled={!file || uploading} sx={uploadButtonSx}>
						{uploading ? <CircularProgress color="inherit" size={18} /> : "Upload"}
					</Button>
					<Button variant="outlined" onClick={downloadTemplate} sx={outlineButtonSx}>
						Download template
					</Button>
					{file ? (
						<Typography variant="body2" color="text.secondary">
							{file.name}
						</Typography>
					) : null}
				</Stack>
			</Box>

			<Box>
				<Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "1.05rem", mb: 1.5 }}>
					Mapping rows{rows.length ? ` · ${rows.length.toLocaleString()}` : ""}
				</Typography>
				{loading && !rows.length ? (
					<Stack direction="row" spacing={1.25} alignItems="center" sx={{ py: 3 }}>
						<CircularProgress size={18} sx={{ color: "#111827" }} />
						<Typography variant="body2" color="text.secondary">
							Loading mapping rows
						</Typography>
					</Stack>
				) : rows.length ? (
					<ResultTable rows={rows} />
				) : (
					<Box
						sx={{
							border: "1px dashed #e5e7eb",
							borderRadius: 2,
							py: 5,
							textAlign: "center",
						}}>
						<Typography sx={{ fontWeight: 600, color: "#111827" }}>No mapping rows yet</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							Upload a file to add uc_invoice_no, uc_mapping_invoice_no, gn_invoice_no, and gn_mapping_invoice_no.
						</Typography>
					</Box>
				)}
			</Box>
		</Stack>
	);
};

export default Mapping;
